/**
 * Mensajes de bloqueo de firma (alineados con backend/utils/bloqueo_firma.py).
 */

export const MSG_DOCUMENTO_RECHAZADO =
  'Uno de los firmantes rechazó este documento. No es posible continuar con las firmas hasta que el propietario lo reenvíe.'

export const TITULO_ERROR_TECNICO = 'No fue posible completar la operación'
export const MSG_ERROR_TECNICO =
  'Ocurrió un inconveniente al procesar la solicitud. Intenta nuevamente. Si el problema continúa, comunícate con soporte.'

/** Códigos funcionales conocidos del backend (no sustituir por genérico). */
export const CODIGOS_FUNCIONALES = new Set([
  'token_invalido',
  'ya_firmado',
  'rechazo_propio',
  'documento_rechazado',
  'cancelado_por_vencimiento',
  'cancelado_otra_causa',
  'en_espera',
  'documento_no_disponible',
  'documento_completado',
])

/** Mensajes funcionales exactos (strings legacy del API sin code). */
const MENSAJES_FUNCIONALES = new Set([
  MSG_DOCUMENTO_RECHAZADO,
  'Token de firma no válido',
  'Documento no encontrado',
  'Este documento ya ha sido firmado',
  'Este documento ya ha sido firmado. El enlace ha expirado.',
  'Este documento ya fue completado y no admite más firmas.',
  'El documento no está disponible para firmar.',
  'Este documento fue cancelado y ya no admite firmas.',
  'Ya rechazaste este documento. Este enlace ya no permite continuar.',
  'Este documento ya ha sido procesado',
  'Aún no es el turno de este firmante',
  'La nota requiere un usuario registrado',
  'No se pudo guardar la nota',
  'Debes crear tu cuenta primero antes de firmar. Por favor, regístrate usando el enlace de tu correo.',
  'No tienes permisos para firmar documentos. Los supervisores no pueden firmar documentos.',
  'No tienes permisos para descargar documentos. Los supervisores no pueden descargar documentos.',
  'No se puede registrar el rechazo sin historial persistido.',
])

const PREFIJOS_FUNCIONALES = [
  'Uno de los firmantes rechazó este documento.',
  'Rechazaste este documento.',
  'El plazo de 5 días para reenviar',
  'Debes esperar a que ',
  'Este documento está en espera.',
  'Ya no es posible firmar',
]

const PATRON_TECNICO =
  /traceback|file\s+"|line \d+|sqlalchemy|pyodbc|psycopg|operationalerror|integrityerror|syntax error|select\s+.+\s+from|insert\s+into|update\s+\w+\s+set|\/app\/|\/root\/|\.py\b|password=|secret=|api[_-]?key|bearer\s+[a-z0-9]|token_firma|connectionstring|stack\s*trace|exception:|error:\s*\w+error/i

function esMensajeFuncional(message) {
  if (!message || typeof message !== 'string') return false
  const t = message.trim()
  if (MENSAJES_FUNCIONALES.has(t)) return true
  if (PREFIJOS_FUNCIONALES.some((p) => t.startsWith(p))) return true
  return false
}

function pareceTecnico(message) {
  if (!message || typeof message !== 'string') return true
  if (PATRON_TECNICO.test(message)) return true
  if (message.length > 320) return true
  if (/[/\\].+\.(py|js|tsx?|sql)\b/i.test(message)) return true
  return false
}

export function errorSeguroUi() {
  return {
    code: 'error_tecnico',
    title: TITULO_ERROR_TECNICO,
    message: MSG_ERROR_TECNICO,
  }
}

/**
 * Interpreta detail de FastAPI de forma segura.
 * Conserva mensajes funcionales conocidos; oculta fallos técnicos/desconocidos.
 *
 * @param {*} detail
 * @param {{ httpStatus?: number }} [opts]
 */
export function parseApiDetail(detail, opts = {}) {
  const httpStatus = opts.httpStatus
  if (typeof httpStatus === 'number' && httpStatus >= 500) {
    return errorSeguroUi()
  }

  if (detail == null) {
    return errorSeguroUi()
  }

  if (typeof detail === 'string') {
    if (esMensajeFuncional(detail) && !pareceTecnico(detail)) {
      return { code: null, title: null, message: detail }
    }
    return errorSeguroUi()
  }

  if (typeof detail === 'object') {
    // Validación FastAPI: lista de errores de campo → genérico seguro
    if (Array.isArray(detail)) {
      return errorSeguroUi()
    }

    const code = detail.code != null ? String(detail.code) : null
    const title = detail.title != null ? String(detail.title) : null
    const rawMessage =
      detail.message ||
      (typeof detail.detail === 'string' ? detail.detail : null) ||
      (typeof detail.msg === 'string' ? detail.msg : null)

    if (code === 'error_tecnico') {
      return errorSeguroUi()
    }

    if (code && CODIGOS_FUNCIONALES.has(code) && rawMessage) {
      const message = String(rawMessage)
      if (pareceTecnico(message)) {
        return errorSeguroUi()
      }
      return {
        code,
        title: title || null,
        message,
      }
    }

    if (rawMessage && esMensajeFuncional(String(rawMessage)) && !pareceTecnico(String(rawMessage))) {
      return {
        code: code || null,
        title: title || null,
        message: String(rawMessage),
      }
    }

    return errorSeguroUi()
  }

  return errorSeguroUi()
}

/**
 * Interpreta la respuesta GET /api/firmar/{token} cuando el flujo no puede continuar.
 * Devuelve null si se puede firmar (salvo EN_ESPERA, que también es bloqueo UI).
 */
export function interpretarRespuestaFirmante(data) {
  if (!data || typeof data !== 'object') return null

  const estado = String(data.estado_turno || '').toUpperCase()
  const codigo = data.codigo_bloqueo || null

  if (data.rechazado === true || estado === 'RECHAZADO' || codigo === 'rechazo_propio') {
    return {
      tipo: 'rechazo_propio',
      titulo: data.titulo_bloqueo || 'Documento rechazado',
      mensaje:
        data.mensaje_bloqueo ||
        data.mensaje_rechazo ||
        'Rechazaste este documento. Este enlace ya no permite firmar.',
      motivo: data.motivo_rechazo || null,
      tituloDocumento: data.titulo_documento || data.documento?.titulo || null,
      puedeFirmar: false,
    }
  }

  if (estado === 'DOCUMENTO_RECHAZADO' || codigo === 'documento_rechazado') {
    return {
      tipo: 'documento_rechazado',
      titulo: data.titulo_bloqueo || 'Documento rechazado',
      mensaje: data.mensaje_bloqueo || MSG_DOCUMENTO_RECHAZADO,
      motivo: null,
      tituloDocumento: data.titulo_documento || data.documento?.titulo || null,
      puedeFirmar: false,
    }
  }

  if (estado === 'CANCELADO_POR_VENCIMIENTO' || codigo === 'cancelado_por_vencimiento') {
    return {
      tipo: 'cancelado_por_vencimiento',
      titulo: data.titulo_bloqueo || 'Cancelado por vencimiento',
      mensaje:
        data.mensaje_bloqueo ||
        'El plazo de 5 días para reenviar este documento ha vencido. Ya no es posible firmar con este enlace.',
      motivo: null,
      tituloDocumento: data.titulo_documento || data.documento?.titulo || null,
      puedeFirmar: false,
    }
  }

  if (estado === 'CANCELADO' || codigo === 'cancelado_otra_causa') {
    return {
      tipo: 'cancelado',
      titulo: data.titulo_bloqueo || 'Documento cancelado',
      mensaje:
        data.mensaje_bloqueo ||
        'Este documento fue cancelado y ya no admite firmas.',
      motivo: null,
      tituloDocumento: data.titulo_documento || data.documento?.titulo || null,
      puedeFirmar: false,
    }
  }

  if (data.en_espera === true && data.es_mi_turno === false) {
    return {
      tipo: 'en_espera',
      titulo: 'Documento en espera',
      mensaje:
        data.mensaje_espera ||
        'Este documento está en espera. Un firmante anterior aún no ha firmado.',
      motivo: null,
      tituloDocumento: data.titulo_documento || data.documento?.titulo || null,
      nombreEsperando: data.nombre_esperando || null,
      ordenFirmante: data.orden_firmante,
      totalFirmantes: data.total_firmantes,
      puedeFirmar: false,
    }
  }

  if (data.bloqueado === true && (data.mensaje_bloqueo || data.titulo_bloqueo)) {
    return {
      tipo: codigo || 'bloqueado',
      titulo: data.titulo_bloqueo || 'Documento no disponible',
      mensaje: data.mensaje_bloqueo || 'El documento no está disponible para firmar.',
      motivo: data.motivo_rechazo || null,
      tituloDocumento: data.titulo_documento || data.documento?.titulo || null,
      puedeFirmar: false,
    }
  }

  return null
}
